// src/pages/inventory/InventoryDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Card,
    CardContent,
    Button,
    Alert,
    CircularProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip as MuiTooltip
} from '@mui/material';
import {
    Inventory as InventoryIcon,
    Warning as WarningIcon,
    TrendingUp as TrendingUpIcon,
    ShoppingCart as ShoppingCartIcon,
    Category as CategoryIcon,
    Warehouse as WarehouseIcon,
    QrCode2 as QrCodeIcon,
    Engineering as EngineeringIcon,
    Add as AddIcon,
    ArrowForward as ArrowForwardIcon,
    SwapVert as SwapVertIcon,
    Refresh as RefreshIcon,
    ArrowDownward as ArrowDownwardIcon,
    ArrowUpward as ArrowUpwardIcon,
    Assessment as AssessmentIcon,
    CheckCircle as CheckCircleIcon,
    ListAlt as ListAltIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const InventoryDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({
        totalItems: 0,
        lowStockItems: 0,
        totalValue: 0,
        pendingPurchases: 0,
        totalCategories: 0,
        totalWarehouses: 0,
        totalSerialized: 0
    });
    const [stockDistribution, setStockDistribution] = useState([]);
    const [recentTransactions, setRecentTransactions] = useState([]);

    const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#64748b'];

    const fetchDashboardData = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };

            // Fetch summary stats
            const statsResponse = await fetch('/api/inventory/stats', { headers });
            if (statsResponse.ok) {
                const statsData = await statsResponse.json();
                setStats(statsData);
            }

            // Fetch stock distribution
            const distributionResponse = await fetch('/api/inventory/distribution', { headers });
            if (distributionResponse.ok) {
                const distributionData = await distributionResponse.json();
                setStockDistribution(distributionData);
            }

            // Fetch recent transactions
            const transactionsResponse = await fetch('/api/inventory/transactions/recent', { headers });
            if (transactionsResponse.ok) {
                const transactionsData = await transactionsResponse.json();
                setRecentTransactions(transactionsData);
            }
        } catch (err) {
            console.error('Error fetching inventory dashboard data:', err);
            setError(err.message || 'Error al cargar datos del dashboard de inventario');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const d = new Date(dateString);
        return d.toLocaleDateString('es-PE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '65vh', gap: 2 }}>
                <CircularProgress size={48} thickness={4} sx={{ color: '#2563eb' }} />
                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                    Cargando dashboard de inventario...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3.5 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '10px' }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Header Hero Bar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3.5, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                        width: 52,
                        height: 52,
                        borderRadius: '14px',
                        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        boxShadow: '0 8px 16px rgba(37, 99, 235, 0.25)'
                    }}>
                        <InventoryIcon sx={{ fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
                                Dashboard de Inventario
                            </Typography>
                            <Chip
                                icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                                label="En Línea"
                                size="small"
                                color="success"
                                sx={{ height: 22, fontSize: '11px', fontWeight: 700, borderRadius: '6px' }}
                            />
                        </Box>
                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mt: 0.2 }}>
                            Control de existencias, valorización, movimientos y órdenes de compra
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/inventory/items?action=new')}
                        startIcon={<AddIcon />}
                        sx={{
                            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                            borderRadius: '9px',
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 2.2,
                            py: 1,
                            boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)',
                            '&:hover': { background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)' }
                        }}
                    >
                        Nuevo Producto
                    </Button>

                    <Button
                        variant="contained"
                        onClick={() => navigate('/inventory/transactions')}
                        startIcon={<SwapVertIcon />}
                        sx={{
                            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                            borderRadius: '9px',
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 2.2,
                            py: 1,
                            boxShadow: '0 4px 14px rgba(124, 58, 237, 0.25)',
                            '&:hover': { background: 'linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)' }
                        }}
                    >
                        Registrar Movimiento
                    </Button>

                    <Button
                        variant="contained"
                        onClick={() => navigate('/inventory/purchases/new')}
                        startIcon={<ShoppingCartIcon />}
                        sx={{
                            background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
                            borderRadius: '9px',
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 2.2,
                            py: 1,
                            boxShadow: '0 4px 14px rgba(13, 148, 136, 0.25)',
                            '&:hover': { background: 'linear-gradient(135deg, #0f766e 0%, #115e59 100%)' }
                        }}
                    >
                        Nueva Compra
                    </Button>

                    <MuiTooltip title="Actualizar datos">
                        <IconButton
                            onClick={() => fetchDashboardData(true)}
                            disabled={refreshing}
                            sx={{
                                border: '1px solid #e2e8f0',
                                borderRadius: '9px',
                                bgcolor: '#fff',
                                p: 1,
                                '&:hover': { bgcolor: '#f1f5f9' }
                            }}
                        >
                            <RefreshIcon sx={{
                                color: '#475569',
                                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                                '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' }
                                }
                            }} />
                        </IconButton>
                    </MuiTooltip>
                </Box>
            </Box>

            {/* KPI Cards Row */}
            <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
                {/* 1. Total Productos */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{
                        borderRadius: '14px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                        transition: 'all 0.25s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(0,0,0,0.07)' },
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '4px',
                            height: '100%',
                            bgcolor: '#3b82f6'
                        }
                    }}>
                        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                <Box>
                                    <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.06em' }}>
                                        TOTAL PRODUCTOS
                                    </Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', my: 0.3, fontSize: '2.2rem' }}>
                                        {stats.totalItems}
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '12px',
                                    bgcolor: 'rgba(59, 130, 246, 0.1)',
                                    color: '#2563eb',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <InventoryIcon sx={{ fontSize: 26 }} />
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px stroke #f1f5f9' }}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                    {stats.totalCategories > 0 ? `${stats.totalCategories} categorías` : 'Catálogo activo'}
                                </Typography>
                                <Button
                                    onClick={() => navigate('/inventory/items')}
                                    size="small"
                                    endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                                    sx={{ textTransform: 'none', fontWeight: 700, color: '#2563eb', p: 0, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
                                >
                                    Ver Catálogo
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 2. Stock Bajo */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{
                        borderRadius: '14px',
                        border: stats.lowStockItems > 0 ? '1px solid #fde68a' : '1px solid #e2e8f0',
                        bgcolor: stats.lowStockItems > 0 ? '#fffbeb' : '#fff',
                        boxShadow: stats.lowStockItems > 0 ? '0 4px 14px rgba(245, 158, 11, 0.12)' : '0 2px 10px rgba(0,0,0,0.03)',
                        transition: 'all 0.25s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: stats.lowStockItems > 0 ? '0 12px 24px rgba(245, 158, 11, 0.2)' : '0 12px 24px rgba(0,0,0,0.07)' },
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '4px',
                            height: '100%',
                            bgcolor: stats.lowStockItems > 0 ? '#f59e0b' : '#10b981'
                        }
                    }}>
                        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                <Box>
                                    <Typography variant="overline" sx={{ color: stats.lowStockItems > 0 ? '#b45309' : '#64748b', fontWeight: 700, letterSpacing: '0.06em' }}>
                                        STOCK BAJO
                                    </Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 800, color: stats.lowStockItems > 0 ? '#d97706' : '#0f172a', my: 0.3, fontSize: '2.2rem' }}>
                                        {stats.lowStockItems}
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '12px',
                                    bgcolor: stats.lowStockItems > 0 ? 'rgba(245, 158, 11, 0.18)' : 'rgba(16, 185, 129, 0.1)',
                                    color: stats.lowStockItems > 0 ? '#d97706' : '#10b981',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <WarningIcon sx={{ fontSize: 26 }} />
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px stroke #f1f5f9' }}>
                                <Chip
                                    label={stats.lowStockItems > 0 ? 'Reabastecer' : 'Stock Saludable'}
                                    size="small"
                                    color={stats.lowStockItems > 0 ? 'warning' : 'success'}
                                    sx={{ height: 22, fontSize: '10px', fontWeight: 800, borderRadius: '6px' }}
                                />
                                <Button
                                    onClick={() => navigate('/inventory/low-stock')}
                                    size="small"
                                    endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                                    sx={{ textTransform: 'none', fontWeight: 700, color: stats.lowStockItems > 0 ? '#b45309' : '#2563eb', p: 0 }}
                                >
                                    Ver Alertas
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 3. Valor Total */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{
                        borderRadius: '14px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                        transition: 'all 0.25s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(0,0,0,0.07)' },
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '4px',
                            height: '100%',
                            bgcolor: '#10b981'
                        }
                    }}>
                        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                <Box>
                                    <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.06em' }}>
                                        VALOR EN LIBROS
                                    </Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#059669', my: 0.3, fontSize: '2rem' }}>
                                        ${stats.totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '12px',
                                    bgcolor: 'rgba(16, 185, 129, 0.1)',
                                    color: '#059669',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <TrendingUpIcon sx={{ fontSize: 26 }} />
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px stroke #f1f5f9' }}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                    {stats.totalSerialized ? `${stats.totalSerialized} eq. serializados` : 'Valor en almacenes'}
                                </Typography>
                                <Button
                                    onClick={() => navigate('/inventory/reports')}
                                    size="small"
                                    endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                                    sx={{ textTransform: 'none', fontWeight: 700, color: '#059669', p: 0 }}
                                >
                                    Ver Reportes
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 4. Compras Pendientes */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{
                        borderRadius: '14px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                        transition: 'all 0.25s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(0,0,0,0.07)' },
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '4px',
                            height: '100%',
                            bgcolor: '#8b5cf6'
                        }
                    }}>
                        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                <Box>
                                    <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.06em' }}>
                                        COMPRAS PENDIENTES
                                    </Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#7c3aed', my: 0.3, fontSize: '2.2rem' }}>
                                        {stats.pendingPurchases}
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '12px',
                                    bgcolor: 'rgba(139, 92, 246, 0.1)',
                                    color: '#7c3aed',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <ShoppingCartIcon sx={{ fontSize: 26 }} />
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px stroke #f1f5f9' }}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                    {stats.totalWarehouses ? `${stats.totalWarehouses} almacenes activos` : 'Por recepcionar'}
                                </Typography>
                                <Button
                                    onClick={() => navigate('/inventory/purchases')}
                                    size="small"
                                    endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                                    sx={{ textTransform: 'none', fontWeight: 700, color: '#7c3aed', p: 0 }}
                                >
                                    Ver Órdenes
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Middle Section: Chart & Quick Access Shortcuts */}
            <Grid container spacing={3} sx={{ mb: 3.5 }}>
                {/* Distribución por Categoría Chart */}
                <Grid item xs={12} md={7} lg={8}>
                    <Paper sx={{
                        p: 3,
                        borderRadius: '14px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                <CategoryIcon sx={{ color: '#2563eb' }} />
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.1rem' }}>
                                    Distribución del Inventario por Categoría
                                </Typography>
                            </Box>
                            <Button
                                onClick={() => navigate('/inventory/categories')}
                                size="small"
                                sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}
                            >
                                Ver Categorías
                            </Button>
                        </Box>

                        {stockDistribution && stockDistribution.length > 0 ? (
                            <Box sx={{ width: '100%', height: 320, mt: 1 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={stockDistribution}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={65}
                                            outerRadius={105}
                                            paddingAngle={4}
                                            dataKey="value"
                                            nameKey="name"
                                        >
                                            {stockDistribution.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={2} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            content={({ active, payload }) => {
                                                if (active && payload && payload.length) {
                                                    const data = payload[0].payload;
                                                    return (
                                                        <Box sx={{ bgcolor: '#0f172a', color: '#fff', p: 1.5, borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{data.name}</Typography>
                                                            <Typography variant="body2" sx={{ color: '#38bdf8' }}>Unidades: <strong>{data.value}</strong></Typography>
                                                            {data.item_count && <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8' }}>{data.item_count} productos en catálogo</Typography>}
                                                        </Box>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                        <Legend
                                            verticalAlign="bottom"
                                            height={36}
                                            formatter={(value) => <span style={{ color: '#334155', fontWeight: 600, fontSize: '13px' }}>{value}</span>}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1, py: 6, textCenter: 'center' }}>
                                <CategoryIcon sx={{ fontSize: 54, color: '#cbd5e1', mb: 1.5 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#475569' }}>
                                    No hay datos de distribución
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2, maxWidth: 300, textAlign: 'center' }}>
                                    Crea productos y asígnales categorías para visualizar la distribución del stock.
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => navigate('/inventory/categories')}
                                    sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
                                >
                                    Gestionar Categorías
                                </Button>
                            </Box>
                        )}
                    </Paper>
                </Grid>

                {/* Accesos Rápidos y Módulos */}
                <Grid item xs={12} md={5} lg={4}>
                    <Paper sx={{
                        p: 3,
                        borderRadius: '14px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                        height: '100%'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2.5 }}>
                            <AssessmentIcon sx={{ color: '#7c3aed' }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.1rem' }}>
                                Accesos Rápidos y Módulos
                            </Typography>
                        </Box>

                        <Grid container spacing={1.5}>
                            <Grid item xs={6}>
                                <Paper
                                    onClick={() => navigate('/inventory/warehouses')}
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        borderRadius: '10px',
                                        border: '1px solid #e2e8f0',
                                        bgcolor: '#fff',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': { borderColor: '#3b82f6', bgcolor: '#f0f9ff', transform: 'translateY(-2px)' }
                                    }}
                                >
                                    <WarehouseIcon sx={{ color: '#2563eb', mb: 1, fontSize: 28 }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                        Almacenes
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                        {stats.totalWarehouses || 0} sedes registradas
                                    </Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={6}>
                                <Paper
                                    onClick={() => navigate('/inventory/categories')}
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        borderRadius: '10px',
                                        border: '1px solid #e2e8f0',
                                        bgcolor: '#fff',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': { borderColor: '#10b981', bgcolor: '#ecfdf5', transform: 'translateY(-2px)' }
                                    }}
                                >
                                    <CategoryIcon sx={{ color: '#10b981', mb: 1, fontSize: 28 }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                        Categorías
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                        {stats.totalCategories || 0} familias de stock
                                    </Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={6}>
                                <Paper
                                    onClick={() => navigate('/inventory/equipment')}
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        borderRadius: '10px',
                                        border: '1px solid #e2e8f0',
                                        bgcolor: '#fff',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': { borderColor: '#8b5cf6', bgcolor: '#f5f3ff', transform: 'translateY(-2px)' }
                                    }}
                                >
                                    <QrCodeIcon sx={{ color: '#8b5cf6', mb: 1, fontSize: 28 }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                        Equipos Serializados
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                        MAC, N/S y Alquiler
                                    </Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={6}>
                                <Paper
                                    onClick={() => navigate('/inventory/kits')}
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        borderRadius: '10px',
                                        border: '1px solid #e2e8f0',
                                        bgcolor: '#fff',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': { borderColor: '#0284c7', bgcolor: '#f0f9ff', transform: 'translateY(-2px)' }
                                    }}
                                >
                                    <ListAltIcon sx={{ color: '#0284c7', mb: 1, fontSize: 28 }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                        Packs / Kits
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                        Combos de Instalación
                                    </Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={6}>
                                <Paper
                                    onClick={() => navigate('/inventory/technicians')}
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        borderRadius: '10px',
                                        border: '1px solid #e2e8f0',
                                        bgcolor: '#fff',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': { borderColor: '#f59e0b', bgcolor: '#fffbeb', transform: 'translateY(-2px)' }
                                    }}
                                >
                                    <EngineeringIcon sx={{ color: '#f59e0b', mb: 1, fontSize: 28 }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                        Técnicos
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                        Stock en cuadrillas
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 2.5, p: 2, borderRadius: '10px', bgcolor: '#f1f5f9', border: '1px stroke #e2e8f0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                                    Reportes y Exportación
                                </Typography>
                                <ListAltIcon sx={{ fontSize: 18, color: '#64748b' }} />
                            </Box>
                            <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600, mb: 1.5 }}>
                                Genera reportes valorizados en PDF/Excel para auditorías y contabilidad.
                            </Typography>
                            <Button
                                fullWidth
                                variant="outlined"
                                size="small"
                                onClick={() => navigate('/inventory/reports')}
                                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, color: '#0f172a', borderColor: '#cbd5e1', bgcolor: '#fff' }}
                            >
                                Abrir Centro de Reportes
                            </Button>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Bottom Section: Recent Transactions Timeline/Table */}
            <Paper sx={{
                p: 3,
                borderRadius: '14px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                        <SwapVertIcon sx={{ color: '#7c3aed' }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.1rem' }}>
                            Movimientos Recientes en Inventario
                        </Typography>
                    </Box>
                    <Button
                        onClick={() => navigate('/inventory/transactions')}
                        size="small"
                        endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                        sx={{ textTransform: 'none', fontWeight: 700, color: '#7c3aed' }}
                    >
                        Ver Historial Completo
                    </Button>
                </Box>

                {recentTransactions && recentTransactions.length > 0 ? (
                    <>
                        {/* Desktop View */}
                        <TableContainer sx={{ borderRadius: '10px', border: '1px solid #f1f5f9', display: { xs: 'none', md: 'block' } }}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Tipo</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Producto</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Cantidad</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Almacén</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Registrado por</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.5 }}>Fecha y Hora</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {recentTransactions.map((tx) => {
                                        const isEntry = tx.transaction_type === 'entrada' || tx.quantity > 0;
                                        return (
                                            <TableRow key={tx.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell>
                                                    <Chip
                                                        icon={isEntry ? <ArrowDownwardIcon sx={{ fontSize: '12px !important' }} /> : <ArrowUpwardIcon sx={{ fontSize: '12px !important' }} />}
                                                        label={isEntry ? 'Entrada' : 'Salida'}
                                                        size="small"
                                                        color={isEntry ? 'success' : 'error'}
                                                        sx={{ height: 22, fontSize: '11px', fontWeight: 800, borderRadius: '6px' }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: '#0f172a' }}>
                                                    {tx.item_name}
                                                    {(tx.brand || tx.model) && (
                                                        <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
                                                            {tx.brand} {tx.model} {tx.sku ? `(SKU: ${tx.sku})` : ''}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: isEntry ? '#059669' : '#dc2626' }}>
                                                    {isEntry ? `+${Math.abs(tx.quantity)}` : `-${Math.abs(tx.quantity)}`}
                                                </TableCell>
                                                <TableCell sx={{ color: '#475569', fontWeight: 500 }}>
                                                    {tx.warehouse_name || 'Almacén Central'}
                                                </TableCell>
                                                <TableCell sx={{ color: '#475569', fontWeight: 500 }}>
                                                    {tx.user_name || 'Sistema'}
                                                </TableCell>
                                                <TableCell sx={{ color: '#64748b', fontSize: '0.82rem' }}>
                                                    {formatDate(tx.date)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Mobile View Card List */}
                        <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
                            {recentTransactions.map((tx) => {
                                const isEntry = tx.transaction_type === 'entrada' || tx.quantity > 0;
                                return (
                                    <Paper key={tx.id} sx={{ p: 1.8, borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                                            <Chip
                                                icon={isEntry ? <ArrowDownwardIcon sx={{ fontSize: '12px !important' }} /> : <ArrowUpwardIcon sx={{ fontSize: '12px !important' }} />}
                                                label={isEntry ? 'Entrada' : 'Salida'}
                                                size="small"
                                                color={isEntry ? 'success' : 'error'}
                                                sx={{ height: 22, fontSize: '10px', fontWeight: 800 }}
                                            />
                                            <Typography variant="body2" sx={{ fontWeight: 800, color: isEntry ? '#059669' : '#dc2626' }}>
                                                {isEntry ? `+${Math.abs(tx.quantity)}` : `-${Math.abs(tx.quantity)}`} unid.
                                            </Typography>
                                        </Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                            {tx.item_name}
                                        </Typography>
                                        {(tx.brand || tx.model) && (
                                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                                {tx.brand} {tx.model}
                                            </Typography>
                                        )}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 0.8, borderTop: '1px solid #f1f5f9' }}>
                                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                {tx.warehouse_name || 'Almacén Central'}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                                {formatDate(tx.date)}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                );
                            })}
                        </Box>
                    </>
                ) : (
                    <Box sx={{ py: 5, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: '10px', border: '1px dashed #cbd5e1' }}>
                        <SwapVertIcon sx={{ fontSize: 42, color: '#cbd5e1', mb: 1 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#475569' }}>
                            Sin movimientos registrados recientemente
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
                            Las entradas y salidas de stock aparecerán listadas aquí en tiempo real.
                        </Typography>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => navigate('/inventory/transactions')}
                            startIcon={<SwapVertIcon />}
                            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}
                        >
                            Registrar Primer Movimiento
                        </Button>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default InventoryDashboard;